import React, { cloneElement, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { Ban, Eye, FileText, MailCheck, ShieldCheck } from "lucide-react";
import betaAPI from "v2/api";
import V2AuthInput from "v2/views/auth/components/V2AuthInput";
import V2AuthButton from "v2/views/auth/components/V2AuthButton";
import ConsentChoice, { RequiredTag } from "./ConsentChoice";
import { StatusCard } from "./JoinLayout";

const today = () => new Date().toISOString().slice(0, 10);

// API error codes with their own message (auth.guardianSignup.errors.*).
const ERROR_CODES = [
  "already_registered",
  "consent_version_changed",
  "invalid_date_of_birth",
  "too_old",
];

const NO_CHOICES = {
  guardian_declaration: false,
  club_tracking: false,
  research: false,
};

const Field = ({ label, children, error }) => {
  const id = useId();
  return (
    <div>
      <label
        htmlFor={id}
        className="mb-1.5 block text-sm font-medium text-[#374151]"
      >
        {label}
      </label>
      {cloneElement(children, { id })}
      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
    </div>
  );
};

const Section = ({ number, title, hint, children }) => (
  <section className="border-t border-[#F1F2F4] px-6 py-7 first:border-t-0 sm:px-8">
    <div className="mb-5 flex items-start gap-3">
      <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-[#FFF1E8] text-sm font-semibold text-[#C2410C]">
        {number}
      </span>
      <div>
        <h2 className="text-[17px] font-semibold leading-7 text-[#111827]">
          {title}
        </h2>
        {hint && <p className="mt-0.5 text-sm text-[#6B7280]">{hint}</p>}
      </div>
    </div>
    {children}
  </section>
);

// "Nothing recorded yet" and "change your mind" are the lead and the steps.
const NOTICE = [
  { key: "responsible", Icon: ShieldCheck },
  { key: "whoSees", Icon: Eye },
  { key: "neverSold", Icon: Ban },
  { key: "requests", Icon: FileText },
];

const STEPS = ["consent", "coach", "change"];

/**
 * A parent or guardian gives consent for their child to join an under-16
 * team. No account: we email them a private link to change their choices.
 */
export default function GuardianSignup({ invite, token }) {
  const { t } = useTranslation();
  const [guardianName, setGuardianName] = useState("");
  const [guardianEmail, setGuardianEmail] = useState("");
  const [childName, setChildName] = useState("");
  const [childDob, setChildDob] = useState("");
  const [choices, setChoices] = useState(NO_CHOICES);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(null);

  const wording = invite.consent;
  const byPurpose = Object.fromEntries(
    wording.choices.map((c) => [c.purpose, c])
  );
  const declaration = byPurpose.guardian_declaration;
  const club = wording.club_name || invite.workspace_name;
  const boxes = wording.choices.filter(
    (c) => c.purpose !== "guardian_declaration"
  );

  const setChoice = (purpose) => (value) =>
    setChoices((prev) => ({ ...prev, [purpose]: value }));

  const canSubmit =
    guardianName.trim() &&
    guardianEmail.trim() &&
    childName.trim() &&
    childDob &&
    wording.choices.every((c) => !c.required || choices[c.purpose]);

  const errorMessage = (err) => {
    const code = err.payload?.code;
    if (ERROR_CODES.includes(code))
      return t(`auth.guardianSignup.errors.${code}`);
    if (err.status === 410) return t("auth.joinTeam.askCoach");
    if (err.status === 400) return t("auth.guardianSignup.errors.checkDetails");
    return t("auth.guardianSignup.errors.generic");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const { data } = await betaAPI.createGuardianClaim({
        token,
        body: {
          guardian: { name: guardianName, email: guardianEmail },
          child: { name: childName, date_of_birth: childDob },
          consents: choices,
          consent_version: wording.version,
        },
      });
      setDone(data.claim);
    } catch (err) {
      console.error("Failed to give consent", err);
      setError(errorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const registerAnother = () => {
    setChildName("");
    setChildDob("");
    setChoices(NO_CHOICES);
    setDone(null);
    window.scrollTo(0, 0);
  };

  if (done) {
    return (
      <StatusCard>
        <div className="text-center">
          <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-[#ECFDF5] text-[#047857]">
            <MailCheck size={26} />
          </div>
          <h1 className="text-2xl font-bold text-[#111827]">
            {t("auth.guardianSignup.done.title", { name: done.guardian_name })}
          </h1>
          <p className="mt-3 text-[15px] leading-relaxed text-[#374151]">
            {t("auth.guardianSignup.done.body", {
              child: done.child_name,
              team: done.team_name,
            })}
          </p>
          <p className="mt-3 text-[15px] leading-relaxed text-[#374151]">
            {t("auth.guardianSignup.done.email", {
              email: done.guardian_email,
            })}
          </p>
          <div className="mt-8">
            <V2AuthButton variant="secondary" onClick={registerAnother}>
              {t("auth.guardianSignup.done.another")}
            </V2AuthButton>
          </div>
        </div>
      </StatusCard>
    );
  }

  return (
    <div className="lg:grid lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] lg:gap-14">
      {/* Left: what this is and what happens to the data */}
      <aside className="mb-8 lg:sticky lg:top-10 lg:mb-0 lg:self-start">
        <p className="text-xs font-semibold uppercase tracking-[0.08em] text-[#C2410C]">
          {invite.workspace_name}
        </p>
        <h1 className="mt-2 text-[28px] font-bold leading-tight text-[#111827] sm:text-4xl">
          {t("auth.guardianSignup.heading", { team: invite.team_name })}
        </h1>
        <p className="mt-3 max-w-md text-base leading-relaxed text-[#4B5563]">
          {t("auth.guardianSignup.lead")}
        </p>

        <ol className="mt-8 space-y-4">
          {STEPS.map((step, i) => (
            <li key={step} className="flex gap-3">
              <span className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full border border-[#E5E7EB] bg-white text-xs font-semibold text-[#374151]">
                {i + 1}
              </span>
              <div>
                <p className="text-[15px] font-semibold text-[#111827]">
                  {t(`auth.guardianSignup.steps.${step}.title`)}
                </p>
                <p className="text-sm leading-relaxed text-[#6B7280]">
                  {t(`auth.guardianSignup.steps.${step}.body`)}
                </p>
              </div>
            </li>
          ))}
        </ol>

        <div className="mt-8 rounded-2xl border border-[#EEF0F2] bg-white p-5">
          <h2 className="text-sm font-semibold text-[#111827]">
            {t("auth.guardianSignup.notice.title")}
          </h2>
          <ul className="mt-3 space-y-3">
            {NOTICE.map(({ key, Icon }) => (
              <li
                key={key}
                className="flex gap-3 text-sm leading-relaxed text-[#4B5563]"
              >
                <Icon
                  size={16}
                  className="mt-0.5 flex-shrink-0 text-[#E07A3A]"
                />
                <span>{t(`auth.guardianSignup.notice.${key}`, { club })}</span>
              </li>
            ))}
          </ul>
          <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2">
            <a
              href={wording.privacy_policy_url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm font-medium text-[#C2410C] underline underline-offset-2"
            >
              {t("auth.guardianSignup.privacyLink")}
            </a>
            {wording.privacy_notice_url && (
              <a
                href={wording.privacy_notice_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm font-medium text-[#C2410C] underline underline-offset-2"
              >
                {t("auth.guardianSignup.clubNoticeLink", { club })}
              </a>
            )}
          </div>
        </div>
      </aside>

      {/* Right: the form */}
      <form
        onSubmit={handleSubmit}
        className="overflow-hidden rounded-2xl border border-[#EEF0F2] bg-white shadow-sm"
      >
        <Section number={1} title={t("auth.guardianSignup.you")}>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={t("auth.guardianSignup.guardianName")}>
              <V2AuthInput
                value={guardianName}
                onChange={(e) => setGuardianName(e.target.value)}
                placeholder={t("auth.guardianSignup.guardianNamePlaceholder")}
                autoComplete="name"
                disabled={submitting}
              />
            </Field>
            <Field label={t("auth.common.email")}>
              <V2AuthInput
                type="email"
                value={guardianEmail}
                onChange={(e) => setGuardianEmail(e.target.value)}
                placeholder={t("auth.common.emailPlaceholder")}
                autoComplete="email"
                disabled={submitting}
              />
            </Field>
          </div>
        </Section>

        <Section
          number={2}
          title={t("auth.guardianSignup.child")}
          hint={t("auth.guardianSignup.childHint")}
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={t("auth.guardianSignup.childName")}>
              <V2AuthInput
                value={childName}
                onChange={(e) => setChildName(e.target.value)}
                placeholder={t("auth.guardianSignup.childNamePlaceholder")}
                autoComplete="off"
                disabled={submitting}
              />
            </Field>
            <Field label={t("auth.guardianSignup.childDob")}>
              <V2AuthInput
                type="date"
                value={childDob}
                max={today()}
                onChange={(e) => setChildDob(e.target.value)}
                disabled={submitting}
              />
            </Field>
          </div>
        </Section>

        <Section
          number={3}
          title={t("auth.guardianSignup.consent")}
          hint={
            boxes.some((c) => !c.required)
              ? t("auth.guardianSignup.consentHint")
              : t("auth.guardianSignup.consentHintRequiredOnly")
          }
        >
          <div className="space-y-3">
            <ConsentChoice
              title={declaration.text || declaration.title}
              badge={<RequiredTag required />}
              checked={choices.guardian_declaration}
              onChange={setChoice("guardian_declaration")}
              disabled={submitting}
            />
            {boxes.map((c) => (
              <ConsentChoice
                key={c.purpose}
                title={c.title}
                text={c.text}
                badge={<RequiredTag required={c.required} />}
                checked={choices[c.purpose]}
                onChange={setChoice(c.purpose)}
                disabled={submitting}
              />
            ))}
          </div>
          <p className="mt-4 text-sm leading-relaxed text-[#6B7280]">
            {t("auth.guardianSignup.age16")}
          </p>
        </Section>

        <div className="border-t border-[#F1F2F4] bg-[#FAFAFB] px-6 py-6 sm:px-8">
          {error && (
            <div
              role="alert"
              className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700"
            >
              {error}
            </div>
          )}
          <V2AuthButton
            type="submit"
            disabled={!canSubmit}
            loading={submitting}
            className="py-3.5 text-base"
          >
            {t("auth.guardianSignup.submit")}
          </V2AuthButton>
          <p className="mt-3 text-center text-xs text-[#6B7280]">
            {canSubmit
              ? t("auth.guardianSignup.emailNote")
              : t("auth.guardianSignup.requiredHint")}
          </p>
        </div>
      </form>
    </div>
  );
}
